import React, { useState } from "react";
import { ArrowLeft, Lock, Smartphone, CloudOff, Cloud, Link2, BarChart3, Info, Download, Trash2, ShieldCheck, KeyRound, FileText, ChevronRight } from "lucide-react";
import { analyticsConfigured, analyticsOptedOut, setAnalyticsOptOut } from "./analytics.js";

// A plain-language account of where data lives and what leaves the device. Keep it true: if a feature changes what is sent,
// change the matching row here.

function Row({ icon: Icon, title, status, children }) {
  return (
    <div className="flex gap-3 py-3.5 border-b border-hair last:border-0">
      <Icon size={17} className="text-brass shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-semibold">{title}</div>
          {status && <span className="text-[10px] px-2 py-0.5 rounded-full shrink-0" style={{ background: "var(--card2)", color: "var(--ivory-dim)" }}>{status}</span>}
        </div>
        <div className="text-xs text-ivory-dim mt-1 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function Section({ title, note, children }) {
  return (
    <div className="mb-5">
      <div className="text-xs uppercase tracking-wide text-ivory-dim mb-1">{title}</div>
      {note && <div className="text-xs text-ivory-dim mb-2 leading-relaxed">{note}</div>}
      <div className="bg-card rounded-2xl px-4">{children}</div>
    </div>
  );
}

export default function PrivacyView({ onBack, signedIn, encOn, lockOn, showBusiness = false, onBackup, onErase, onOpenAccount, onOpenLegal }) {
  const [optedOut, setOptedOut] = useState(analyticsOptedOut());
  const toggle = () => { const next = !optedOut; setAnalyticsOptOut(next); setOptedOut(next); };

  return (
    <div className="px-5 pt-8 pb-28">
      <div className="flex items-center gap-3 mb-5">
        <button onClick={onBack} className="p-1 -ml-1" aria-label="Back"><ArrowLeft size={18} className="text-ivory-dim" /></button>
        <h2 className="v-display text-xl font-semibold">Privacy</h2>
      </div>

      <div className="rounded-2xl p-5 mb-6" style={{ background: "var(--brass-dim)" }}>
        <Lock size={20} className="text-brass mb-2" />
        <div className="v-display text-lg font-semibold leading-snug">Your money is your business.</div>
        <p className="text-xs mt-2 leading-relaxed">Balance &amp; Beyond works fully offline with no account. Your entries live on this device. We don't sell your data. Below is exactly what stays here, what can leave and when, and the controls you have.</p>
      </div>

      <Section title="Stays on this device">
        <Row icon={Smartphone} title="Your records">{`Profiles, entries, ${showBusiness ? "invoices, " : ""}budgets, goals, notes and portfolio holdings are stored in this app on this device.`} Nothing is uploaded unless you turn on one of the options below.</Row>
        <Row icon={CloudOff} title="No account needed">You can use everything that doesn't need the internet without signing up for anything.</Row>
        <Row icon={KeyRound} title="App lock" status={lockOn ? "On" : "Off"}>Ask for a 4 or 6 digit PIN (or your fingerprint or face) when the app opens or comes back from the background. If you forget it, a code sent to your account email resets it without erasing anything. It stops people who pick up your unlocked phone. It isn't encryption of the data on the device, so keep your phone's own lock screen on too. Turn it on in Settings.</Row>
      </Section>

      <Section title="Leaves this device only if you choose" note="These happen only when you use them. Other features that send limited information when you use them, such as prices, AI and email, are described in the Privacy Policy below.">
        <Row icon={Cloud} title="Account &amp; sync" status={signedIn ? "On" : "Off"}>Your email and password, plus a copy of your data so it follows you to your other devices. Sign out to stop syncing; you can also delete the account and everything stored online for it.</Row>
        <Row icon={ShieldCheck} title="End-to-end encryption" status={encOn ? "On" : "Optional"}>
          {encOn
            ? "Your online copy is encrypted on this device with a passphrase only you know, before it's uploaded. We hold no key and can't read it. If you forget the passphrase it can't be recovered."
            : "Without it, your online copy is protected by account rules but can technically be read by whoever runs the database. Turn on end-to-end encryption in Account & sync and it's scrambled on your device with a passphrase only you know, so we can't read it."}
          {signedIn && <button onClick={onOpenAccount} className="block mt-2 text-brass font-medium underline">{encOn ? "Manage in Account & sync" : "Turn it on in Account & sync"}</button>}
        </Row>
        {showBusiness && <Row icon={Link2} title="Sharing an invoice">If you send an invoice by link or email, a copy of that invoice is stored online, readable, so your client can open it. This stays readable even with encryption on. Anyone who has the link can view it.</Row>}
      </Section>

      {analyticsConfigured && (
        <Section title="Usage counts">
          <Row icon={BarChart3} title="Anonymous usage counts" status={optedOut ? "Off" : "On"}>
            We count simple things like "app opened" and "profile created" to see whether the app is working. Never amounts, categories, notes, names or anything from your records. Like any online service, the provider receiving them can see your device's internet address.
            <button role="switch" aria-checked={!optedOut} onClick={toggle} className="mt-3 flex items-center gap-2.5 text-xs font-medium text-ivory">
              <span className="relative inline-block w-10 h-6 rounded-full transition-colors" style={{ background: optedOut ? "var(--card2)" : "var(--brass)" }}>
                <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all" style={{ left: optedOut ? 2 : 18 }} />
              </span>
              {optedOut ? "Usage counts are off" : "Share anonymous usage counts"}
            </button>
          </Row>
        </Section>
      )}

      <Section title="Good to know" note="We'd rather tell you the limits than leave you to guess.">
        <Row icon={Info} title="The app lock is a screen lock">It keeps casual snoopers out but doesn't encrypt what's saved on the device. A forgotten PIN can only be reset by erasing this device's data.</Row>
        <Row icon={Info} title="Back-ups are your responsibility">If you never sync, your data exists only on this device. Clearing your browser data or losing the phone loses it, unless you've backed up. With encryption on, a forgotten passphrase also means the online copy is unrecoverable.</Row>
      </Section>

      <Section title="Your controls">
        <div className="py-3.5 border-b border-hair">
          <button onClick={onBackup} className="w-full flex items-center justify-center gap-2 bg-card2 rounded-full py-2.5 text-sm font-medium"><Download size={15} className="text-brass" /> Back up my data</button>
          <div className="text-xs text-ivory-dim mt-2 leading-relaxed">Saves a copy of everything to a file you keep.</div>
        </div>
        <div className="py-3.5">
          <button onClick={onErase} className="w-full flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-medium" style={{ background: "var(--card2)", color: "var(--rust)" }}><Trash2 size={15} /> Erase all data on this device</button>
          <div className="text-xs text-ivory-dim mt-2 leading-relaxed">Removes every profile and entry from this device. If you're signed in to sync, your online copy follows and is emptied too. To remove the account itself, use Delete account.</div>
        </div>
        {signedIn && (
          <div className="py-3.5 border-t border-hair">
            <button onClick={onOpenAccount} className="w-full flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-medium" style={{ background: "var(--card2)", color: "var(--rust)" }}><Trash2 size={15} /> Delete my account</button>
            <div className="text-xs text-ivory-dim mt-2 leading-relaxed">Permanently deletes your login and everything stored online. It's in Account &amp; sync, at the bottom.</div>
          </div>
        )}
      </Section>

      <Section title="Legal">
        {[["privacy", "Privacy Policy", "How we handle everything, in full"], ["terms", "Terms & Conditions", "The rules for using the app"]].map(([k, t, d]) => (
          <button key={k} onClick={() => onOpenLegal(k)} className="w-full flex items-center gap-3 py-3.5 text-left border-b border-hair last:border-0">
            <FileText size={17} className="text-brass shrink-0" />
            <div className="flex-1"><div className="text-sm font-semibold">{t}</div><div className="text-xs text-ivory-dim mt-0.5">{d}</div></div>
            <ChevronRight size={16} className="text-ivory-dim shrink-0" />
          </button>
        ))}
      </Section>
    </div>
  );
}
